//#region Imports

// @angular
import {inject, Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, Router} from '@angular/router';

// rxjs
import {delay, firstValueFrom} from 'rxjs';

//@ew
import {RouteName} from '@ew/enums';
import {GlobalUser} from '@ew/models';
import {AuthHttpService} from '@ew/auth-http';
import {EW_TOASTER} from '@ew/ew-injects';
import {ToasterType} from '@ew/ew-services-interfaces';
import {GlobalUserStore} from '@ew/global-user';
import {APP_CONFIG} from '@ew/app-config';

//#endregion

/**
 * Authentication Service - Core authentication logic for the Enterwait application.
 *
 * This service handles all authentication-related functionality including:
 * - Validating user authentication status
 * - Managing authentication tokens (localStorage and URL-based)
 * - Fetching and storing authenticated user information
 * - Handling unauthorized access and redirects
 * - Managing profile completion flow
 *
 * The service works in conjunction with:
 * - AuthHttpService: For making authentication API calls
 * - GlobalUserStore: For storing authenticated user state
 * - Router: For navigation and redirects
 *
 * Authentication Flow:
 * 1. Check for token in URL query params (OAuth/SSO flow)
 * 2. Check for token in localStorage
 * 3. Validate token with backend API
 * 4. Store user information globally
 * 5. Check if profile setup is complete
 * 6. Allow or deny access based on authentication status
 *
 * @example
 * // In a component or guard
 * constructor(private authService: AuthService) {}
 *
 * async checkAuth() {
 *   const isAuth = await this.authService.isAuthenticated(route);
 *   if (isAuth) {
 *     // User is authenticated
 *   }
 * }
 *
 * @see AuthHttpService - HTTP service for authentication API calls
 * @see GlobalUserStore - Store for authenticated user data
 * @see authGuard - Route guard using this service
 */
@Injectable({providedIn: 'root'})
export class AuthService {
  //#region Properties

  private readonly toastService = inject(EW_TOASTER);
  private readonly authHttpService = inject(AuthHttpService);
  private readonly router = inject(Router);
  private globalUserStore = inject(GlobalUserStore);
  private readonly appConfig = inject(APP_CONFIG);

  //#endregion

  //#region Methods

  /**
   * Checks if the user is authenticated.
   * If not authenticated, redirects to login and clears token.
   * If authenticated, stores user information globally.
   *
   * @return {Promise<any>} Resolves to true if authenticated, otherwise false.
   */
  public async isAuthenticated(route: ActivatedRouteSnapshot): Promise<boolean> {
    // check if user is authenticated from url
    const isTokenFromUrl = this.tryToSetTokenFromUrl(route);

    // remove token from url and return new route, fix for calling isAuthenticated twice*
    if (isTokenFromUrl) {
      // Create a new object with all queryParams except accessToken
      const queryParams = {...route.queryParams};
      delete queryParams['accessToken'];

      return this.router.navigate([], {
        queryParams,
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    const token = localStorage.getItem('token');

    if (!token) {
      this.handleUnauthorized();
    }

    try {
      const globalUser = await this.fetchAuthenticatedUser(token!);

      if (!globalUser.authenticated) {
        return this.handleUnauthorized();
      }

      // Format the photoPath with base URL if it exists
      if (globalUser.user.photoPath) {
        globalUser.user.photoPath = `${this.appConfig.baseURL}${globalUser.user.photoPath}`;
      }

      this.globalUserStore.setGlobalUser(globalUser);

      // check role exist
      if (!globalUser.user.role) {
        this.router.navigateByUrl(RouteName.CreateProfile);
        return false;
      }

      return true;
    } catch (err) {
      return this.handleUnauthorized();
    }
  }

  /**
   * Tries to set the token from the URL.
   *
   * @param route ActivatedRouteSnapshot - The activated route snapshot.
   * @returns boolean - Whether the token was set or not.
   */
  private tryToSetTokenFromUrl = (route: ActivatedRouteSnapshot): boolean => {
    const token = route.queryParams['accessToken'];

    if (token) {
      localStorage.setItem('token', token);
      return true;
    }

    return false;
  };

  /**
   * Fetches the authenticated user from the API.
   *
   * @param token string - The user's authentication token.
   * @returns Promise<GlobalUser> - The authenticated user object.
   */
  private async fetchAuthenticatedUser(token: string): Promise<GlobalUser> {
    return firstValueFrom(this.authHttpService.isAuthenticated(token));
  }

  /**
   * Refreshes the current user's data from the backend.
   * Useful after profile updates to ensure the UI reflects the latest data.
   *
   * @returns Promise<boolean> - True if refresh successful, false otherwise.
   */
  public async refreshCurrentUser(): Promise<boolean> {
    const token = localStorage.getItem('token');

    if (!token) {
      return false;
    }

    try {
      const globalUser = await this.fetchAuthenticatedUser(token);

      if (!globalUser.authenticated) {
        return false;
      }

      // Format the photoPath with base URL if it exists
      if (globalUser.user.photoPath) {
        globalUser.user.photoPath = `${this.appConfig.baseURL}${globalUser.user.photoPath}`;
      }

      this.globalUserStore.setGlobalUser(globalUser);
      return true;
    } catch (err) {
      console.error('Failed to refresh user data', err);
      return false;
    }
  }

  /**
   * Handles unauthorized access by redirecting to the login page,
   * showing a notification, and clearing the token.
   *
   * @returns {false} - Always returns false to indicate unauthenticated state.
   */
  private handleUnauthorized(): false {
    localStorage.removeItem('token');
    this.router.navigateByUrl(RouteName.Login);

    this.toastService.displayToast(
      'Unauthorized, please login to continue',
      ToasterType.Error,
      'Close',
      {
        duration: 3000,
      },
    );

    return false;
  }

  //#endregion
}
